import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Edit3, Trash2, Search, ShieldCheck, UserCog } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Administrador", operador: "Operador" };

export default function BackofficePage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        role: "operador",
        is_active: true,
    });
    const [saving, setSaving] = useState(false);

    async function load() {
        try {
            const res = await api.get("/admin/users");
            setUsers(res.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar utilizadores");
        }
    }
    useEffect(() => {
        load();
    }, []);

    function openNew() {
        setEditing(null);
        setForm({ name: "", email: "", password: "", role: "operador", is_active: true });
        setOpen(true);
    }

    function openEdit(u) {
        setEditing(u);
        setForm({
            name: u.name,
            email: u.email,
            password: "",
            role: u.role,
            is_active: u.is_active,
        });
        setOpen(true);
    }

    async function save() {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Nome e email são obrigatórios.");
            return;
        }
        if (!editing && form.password.length < 6) {
            toast.error("Palavra-passe deve ter pelo menos 6 caracteres.");
            return;
        }
        if (editing && form.password && form.password.length < 6) {
            toast.error("Palavra-passe deve ter pelo menos 6 caracteres.");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const payload = {
                    name: form.name,
                    email: form.email,
                    role: form.role,
                    is_active: form.is_active,
                };
                if (form.password) payload.password = form.password;
                await api.put(`/admin/users/${editing.id}`, payload);
                toast.success("Utilizador atualizado.");
            } else {
                await api.post("/admin/users", form);
                toast.success("Utilizador criado.");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    async function remove(u) {
        if (u.id === currentUser?.id) {
            toast.error("Não pode eliminar a sua própria conta.");
            return;
        }
        if (!window.confirm(`Eliminar o utilizador "${u.name}"?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("Utilizador eliminado.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    async function toggleActive(u) {
        if (u.id === currentUser?.id) {
            toast.error("Não pode desativar a sua própria conta.");
            return;
        }
        try {
            await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
            toast.success(`Utilizador ${!u.is_active ? "ativado" : "desativado"}.`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    const filtered = users.filter((u) =>
        [u.name, u.email, ROLE_LABELS[u.role]].join(" ").toLowerCase().includes(q.toLowerCase())
    );

    const adminCount = users.filter((u) => u.role === "admin" && u.is_active).length;
    const operadorCount = users.filter((u) => u.role === "operador").length;

    return (
        <>
            <PageHeader
                eyebrow="Administração"
                title="Backoffice"
                description="Gestão de colaboradores e permissões de acesso à oficina."
                testid="backoffice-header"
                actions={
                    <Button
                        onClick={openNew}
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="new-user-btn"
                    >
                        <Plus className="mr-2 h-4 w-4" /> Novo colaborador
                    </Button>
                }
            />

            <section className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="users-stats">
                <StatCard
                    icon={ShieldCheck}
                    label="Administradores ativos"
                    value={adminCount}
                    testid="stat-admins"
                />
                <StatCard
                    icon={UserCog}
                    label="Operadores"
                    value={operadorCount}
                    testid="stat-operadores"
                />
                <StatCard
                    icon={UserCog}
                    label="Total de utilizadores"
                    value={users.length}
                    testid="stat-total-users"
                />
            </section>

            <div className="mb-4 flex max-w-md items-center gap-2 border border-zinc-200 bg-white px-3">
                <Search className="h-4 w-4 text-zinc-400" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Procurar por nome, email ou papel..."
                    className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
                    data-testid="users-search-input"
                />
            </div>

            <div className="border border-zinc-200 bg-white" data-testid="users-table">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    <div className="col-span-3">Nome</div>
                    <div className="col-span-4">Email</div>
                    <div className="col-span-2">Papel</div>
                    <div className="col-span-2">Ativo</div>
                    <div className="col-span-1 text-right">Ações</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500">
                        Sem utilizadores.
                    </div>
                ) : (
                    filtered.map((u) => (
                        <div
                            key={u.id}
                            data-testid={`user-row-${u.id}`}
                            className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-4 py-3 text-sm hover:bg-zinc-50"
                        >
                            <div className="col-span-3">
                                <div className="font-medium text-zinc-900">
                                    {u.name}
                                    {u.id === currentUser?.id ? (
                                        <span className="ml-2 rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
                                            Você
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="col-span-4 truncate text-zinc-700">{u.email}</div>
                            <div className="col-span-2">
                                <span
                                    className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                                        u.role === "admin"
                                            ? "border-zinc-300 bg-zinc-900 text-white"
                                            : "border-zinc-200 bg-zinc-50 text-zinc-700"
                                    }`}
                                >
                                    {ROLE_LABELS[u.role] || u.role}
                                </span>
                            </div>
                            <div className="col-span-2">
                                <Switch
                                    checked={u.is_active}
                                    onCheckedChange={() => toggleActive(u)}
                                    disabled={u.id === currentUser?.id}
                                    data-testid={`user-active-toggle-${u.id}`}
                                />
                                <span className="ml-2 text-xs text-zinc-600">
                                    {u.is_active ? "Ativo" : "Inativo"}
                                </span>
                            </div>
                            <div className="col-span-1 flex justify-end gap-1">
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm"
                                    onClick={() => openEdit(u)}
                                    data-testid={`user-edit-${u.id}`}
                                >
                                    <Edit3 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm text-red-600 hover:text-red-700"
                                    onClick={() => remove(u)}
                                    disabled={u.id === currentUser?.id}
                                    data-testid={`user-delete-${u.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit']">
                            {editing ? "Editar colaborador" : "Novo colaborador"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Nome *
                            </Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="user-name-input"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Email *
                            </Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="user-email-input"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Palavra-passe {editing ? "(deixe vazio para manter)" : "*"}
                            </Label>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                placeholder={editing ? "••••••" : "Mínimo 6 caracteres"}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="user-password-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Papel
                            </Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v })}
                                disabled={editing?.id === currentUser?.id}
                            >
                                <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="user-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="operador">Operador</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Estado
                            </Label>
                            <div className="mt-2.5 flex items-center gap-3">
                                <Switch
                                    checked={form.is_active}
                                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                                    disabled={editing?.id === currentUser?.id}
                                    data-testid="user-active-switch"
                                />
                                <span className="text-sm text-zinc-700">
                                    {form.is_active ? "Ativo" : "Inativo"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="user-save-btn"
                            className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        >
                            {saving ? "A guardar..." : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function StatCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    {label}
                </div>
                <Icon className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-['Outfit'] text-3xl font-semibold text-zinc-900">{value}</div>
        </div>
    );
}
